import { formatDate } from "@clipboard-health/date-time";
import { type UseModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { DeploymentEnvironmentName, environmentConfig } from "@src/appV2/environment";
import { isCapacitorPlatform, useToast } from "@src/appV2/lib";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { getClockInTimingInfo } from "@src/appV2/redesign/utils/clockInTiming";
import { formatTimeWithTimezoneDisambiguation } from "@src/appV2/redesign/utils/formatTimeWithTimezoneDisambiguation";
import { type ShiftStateData } from "@src/appV2/Shifts/Shift/ShiftState/types";
import { type WorkerShift } from "@src/appV2/Shifts/Shift/types";
import { isSameDay } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import { useCallback } from "react";

export function useOnClockIn({
  shift,
  shiftState,
  currentDate,
  clockInModalState,
  tooFarAwayModalState,
  inside,
  onWillOpenClockInModal,
}: {
  shift: WorkerShift | undefined;
  shiftState: ShiftStateData | undefined;
  currentDate: Date;
  clockInModalState: UseModalState;
  tooFarAwayModalState: UseModalState;
  inside: boolean;
  onWillOpenClockInModal?: () => void;
}): () => void {
  const { showErrorToast, showWarningToast } = useToast();

  const userTimezone = new Intl.DateTimeFormat().resolvedOptions().timeZone;
  const facilityTimezone = shift?.facility.tmz ?? userTimezone;

  const shiftStartDate = isDefined(shift)
    ? utcToZonedTime(shift.start, facilityTimezone)
    : undefined;
  const isClockInForbidden =
    !isCapacitorPlatform() &&
    environmentConfig.REACT_APP_ENVIRONMENT_NAME === DeploymentEnvironmentName.PRODUCTION;

  const { isAfterEarliestClockInTime, earliestClockInTime } =
    isDefined(shift) && isDefined(shiftState)
      ? getClockInTimingInfo({
          shiftStart: shift.start,
          shiftState,
          currentDate,
        })
      : { isAfterEarliestClockInTime: undefined, earliestClockInTime: undefined };

  const isShiftToday = isDefined(shiftStartDate)
    ? isSameDay(shiftStartDate, currentDate)
    : undefined;

  return useCallback(() => {
    if (
      !isDefined(shift) ||
      !isDefined(shiftState) ||
      !isDefined(shiftStartDate) ||
      !isDefined(earliestClockInTime) ||
      !isDefined(isShiftToday)
    ) {
      logEvent(APP_V2_USER_EVENTS.CLOCK_IN_FAILED as string, {
        shiftId: shift?._id ?? "unknown",
        workerId: shift?.agentId ?? "unknown",
        reason: "missing_required_data",
        missingData: {
          shift: !isDefined(shift),
          shiftState: !isDefined(shiftState),
          shiftStartDate: !isDefined(shiftStartDate),
          earliestClockInTime: !isDefined(earliestClockInTime),
          isShiftToday: !isDefined(isShiftToday),
        },
      });

      showErrorToast(
        "We couldn't clock you in right now. Please try again, and if the problem persists, contact support."
      );
      return;
    }

    if (isClockInForbidden) {
      showErrorToast("Clock in is only available on the mobile app.");
      return;
    }

    if (!isAfterEarliestClockInTime) {
      logEvent(APP_V2_USER_EVENTS.CLOCK_IN_TOO_EARLY, {
        shiftId: shift._id,
        workerId: shift.agentId,
        earliestClockInTime: earliestClockInTime.toISOString(),
        facilityTimezone,
        userTimezone,
      });

      const dateText = isShiftToday ? "" : ` on ${formatDate(shiftStartDate)}`;
      const formattedClockInTime = formatTimeWithTimezoneDisambiguation({
        time: earliestClockInTime,
        displayInTimezone: facilityTimezone,
        compareToTimezone: userTimezone,
      });

      showWarningToast(
        `You'll be able to clock in starting at ${formattedClockInTime.replace(
          / /g,
          // non-breaking space, ensures time stays on the same line
          "\u00A0"
        )}${dateText}`
      );

      return;
    }

    // Check geofence status before opening clock in modal
    if (!inside) {
      logEvent(APP_V2_USER_EVENTS.CLOCK_IN_OUTSIDE_GEOFENCE, {
        shiftId: shift._id,
        workerId: shift.agentId,
      });
      tooFarAwayModalState.openModal();
      return;
    }

    onWillOpenClockInModal?.();
    clockInModalState.openModal();
  }, [
    clockInModalState,
    tooFarAwayModalState,
    inside,
    isClockInForbidden,
    shift,
    shiftState,
    earliestClockInTime,
    isAfterEarliestClockInTime,
    isShiftToday,
    shiftStartDate,
    showErrorToast,
    facilityTimezone,
    userTimezone,
    showWarningToast,
    onWillOpenClockInModal,
  ]);
}
